import React, { useEffect, useRef, useState, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { fetchDecodedPeaks, getCachedDecoded, setCachedDecoded, downsamplePeaks } from '../utils/peaksCache';
import { getCachedYoutubeId } from '../utils/lazyResolveCache';

// Simple concurrency limiter — max 3 concurrent loads.
// Generation-tagged: resetWaveformQueue() used to zero `active` while loads
// were still in flight, so their finally-decrements drove the counter
// NEGATIVE and the 3-cap was defeated for the rest of the session (every
// row then piled onto the main process's 2-slot yt-dlp queue and timed out).
// A reset now bumps the generation instead: stale loads settle without
// touching the new generation's counter, and queued-but-never-started
// entries resolve immediately instead of leaking their promise.
let active = 0;
const waiting = [];
let generation = 0;

function withLimit(fn) {
    const gen = generation;
    return new Promise((resolve) => {
        const run = () => {
            if (gen !== generation) { resolve(); return; } // reset while queued — row is gone
            active++;
            fn().finally(() => {
                if (gen === generation) {
                    active--;
                    if (waiting.length > 0) waiting.shift()();
                }
                resolve();
            });
        };
        if (active < 3) {
            run();
        } else {
            waiting.push(run);
        }
    });
}

export function resetWaveformQueue() {
    generation++;
    active = 0;
    // Settle queued entries under the NEW generation: each sees a stale
    // gen tag and resolves without running its load.
    waiting.splice(0).forEach((run) => run());
}

// Stagger queue for WaveSurfer.create calls — at most one per animation
// frame across ALL FeedWaveform instances. When several peaks decodes
// resolve close together (especially right after refocus when a backlog
// of downloads completes), the synchronous chain of cache event → match
// → createWs from multiple rows previously ran back-to-back in a single
// task, producing a ~500ms LONG TASK on the renderer. Staggering one per
// rAF lets the browser paint and process input between instantiations.
// Single-row case (most common) is unchanged in cost — one queued entry
// drains the next frame, ~16ms delay, imperceptible.
//
// VISIBILITY GATE: queue paused while document.hidden. peaks downloads
// in flight when the user cmd-tabbed away complete during background,
// queuing createWs calls. If we drained during background, the work
// piled into the refocus moment (the same "freeze on refocus" pattern
// users keep reporting). With the gate, the queue grows while hidden
// but doesn't run; on visibilitychange→visible we resume draining one
// per frame, so waveforms appear progressively after refocus instead
// of all at once.
const _wsQueue = [];
let _wsDraining = false;
let _wsPausedHidden = false;
function _drainWs() {
    if (typeof document !== 'undefined' && document.hidden) {
        // Stop here — visibilitychange→visible will resume.
        _wsPausedHidden = true;
        _wsDraining = false;
        return;
    }
    if (_wsQueue.length === 0) { _wsDraining = false; return; }
    const fn = _wsQueue.shift();
    try { fn(); } catch (e) { console.warn('[wf] stagger callback failed', e); }
    if (_wsQueue.length > 0) requestAnimationFrame(_drainWs);
    else _wsDraining = false;
}
function staggerCreateWs(fn) {
    _wsQueue.push(fn);
    if (!_wsDraining) {
        _wsDraining = true;
        requestAnimationFrame(_drainWs);
    }
}
if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) return;
        if (!_wsPausedHidden) return;
        if (_wsQueue.length === 0) { _wsPausedHidden = false; return; }
        _wsPausedHidden = false;
        if (!_wsDraining) {
            _wsDraining = true;
            requestAnimationFrame(_drainWs);
        }
    });
}

// `mode` (server flag FEED_WAVEFORM_MODE, see utils/feedWaveform.js):
//   FULL   → eager full-track peaks (youtube/SC, else sample). Historical.
//   SAMPLE → eager, but peaks come from the Apple sample only (never yt-dlp).
//   FAKE   → don't load until this track is the active player track; then the
//            real (full) waveform loads and swaps in on top of the parent's
//            FakeTrackWaveform.
// `previewId` is the row's DJPalAudioPreview id; lists other than For You pass
// their own (Likes uses `liked-N`) so play events reach the right row.
const FeedWaveform = ({ src, youtubeId, soundcloudUrl, trackId, index = 0, playbackBlocked = false, mode = 'FULL', previewId: previewIdProp }) => {
    const containerRef = useRef(null);
    const wsRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [failed, setFailed] = useState(false);
    const rafRef = useRef(null);
    const activeRef = useRef(false);
    const previewId = previewIdProp || `feed-${index}`;
    const lazy = mode === 'FAKE';
    const sampleOnly = mode === 'SAMPLE';
    // The row's track is ALREADY the player's when the row mounts (reopening
    // Likes / For You mid-play): load its real waveform straight away and keep
    // the placeholder hidden, instead of placeholder → loader → real (a flash).
    const [activeAtMount] = useState(() => {
        const audio = window.musicPlayerInstance;
        if (!(audio && audio.src)) return false;
        return (!!src && window.__currentPlayerMusicSrc === src)
            || (trackId != null && window.__currentPlayerTrackId === String(trackId));
    });
    // FULL/SAMPLE load eagerly; FAKE waits until the user requests play.
    const [shouldLoad, setShouldLoad] = useState(!lazy || activeAtMount);
    // Bumped to re-run the load effect on a retry (FAKE: a fresh play-click
    // after a prior load failed).
    const [loadNonce, setLoadNonce] = useState(0);
    // Whether the CURRENT paint used real (yt-dlp) peaks, vs the sample /
    // silhouette fallback — late-arriving real peaks upgrade a fallback.
    const renderedRealRef = useRef(false);
    // Which peaks-source the current `ready`/`failed` belong to — lets us reset
    // them SYNCHRONOUSLY when the source flips (see below).
    const [loadedKey, setLoadedKey] = useState(null);
    // True when the current paint came from already-decoded peaks (remount
    // after back-nav): skip the 0.3s opacity fade so the waveform is just
    // *there*, like it was before navigating away. Network arrivals keep the
    // fade — that's the case it was designed for.
    const instantRef = useRef(false);
    // FAKE rows defer drawing the sample while a lazy YouTube resolve may still
    // land (so we don't flash the sample then swap to the real track). This
    // flips true once we've waited long enough that the track is probably
    // sample-only, at which point the sample is drawn.
    const [sampleWaitElapsed, setSampleWaitElapsed] = useState(false);
    const readyRef = useRef(false);
    readyRef.current = ready;
    // If the server flag flips to an eager mode mid-session, start loading
    // even if this row mounted under FAKE. (FAKE never force-unloads — once
    // a row has its real waveform, keep it.)
    useEffect(() => { if (!lazy) setShouldLoad(true); }, [lazy]);

    // FAKE mode: kick the load (→ loader shows) the instant the user requests
    // play. djpal-row-play fires on click — before extraction/playback — so the
    // loading animation starts immediately rather than only once the track is
    // actually playing. If a previous attempt failed (fake is showing again),
    // clear the failure and bump the nonce so this click retries the load.
    useEffect(() => {
        if (!lazy) return undefined;
        const startLoad = () => {
            setShouldLoad(true);
            if (!readyRef.current) { setFailed(false); setLoadNonce((n) => n + 1); }
        };
        const onRowPlay = (e) => {
            if (e.detail !== previewId) return;
            startLoad();
        };
        // Natural auto-advance: advanceListContext (MusicPlayer) fires this the
        // moment it STARTS resolving the next item — before the yt-dlp extract
        // that gates onAudioPlay/player-track-change. Without it the loader on
        // an auto-advanced row would only appear once the track actually began
        // playing (seconds later), unlike the manual click path above. Matched
        // by sample src / trackId since advanceListContext is generic and has
        // no notion of this row's positional previewId.
        const onPreload = (e) => {
            const d = e.detail || {};
            const matches = (d.src && src && d.src === src)
                || (d.trackId != null && trackId != null && String(d.trackId) === String(trackId));
            if (!matches) return;
            startLoad();
        };
        window.addEventListener('djpal-row-play', onRowPlay);
        window.addEventListener('feed-row-preload', onPreload);
        return () => {
            window.removeEventListener('djpal-row-play', onRowPlay);
            window.removeEventListener('feed-row-preload', onPreload);
        };
    }, [lazy, previewId, src, trackId]);

    // Mirror DJPalAudioPreview: track any lazy-resolved youtubeId for this
    // trackId so when a click resolves a HIGH match, the waveform repaints
    // with the YT-track peaks instead of staying on the sample's silhouette.
    const [resolvedYoutubeId, setResolvedYoutubeId] = useState(() => getCachedYoutubeId(trackId));
    // The URL the PLAYER is actually streaming for THIS track, when it
    // differs from what we'd derive ourselves — the dead-DB-link case: the
    // row's youtubeId prop points at a dead video, playback silently swapped
    // in a working one, and both src- and originalUrl-matching fail. The
    // player stashes/broadcasts its trackId exactly so this row can adopt
    // the live URL (whose peaks the bottom player has already decoded).
    const [adoptedUrl, setAdoptedUrl] = useState(() => (
        (trackId != null
            && window.__currentPlayerTrackId === String(trackId)
            && window.__currentPlayerOriginalUrl)
        || null
    ));
    // When playback for THIS row's track falls back to the Apple sample (its
    // YT/SC stream failed to resolve — nsig miss, dead link, rate-limit), the
    // bottom player plays the sample with a null originalUrl and decodes it for
    // the now-playing waveform. Mirror that here so the feed row also draws the
    // sample instead of pinning on a slow/failing yt-dlp peaks fetch that
    // 30s-times-out to the generated silhouette.
    const [fellToSample, setFellToSample] = useState(false);

    useEffect(() => {
        if (!trackId) return undefined;
        const tid = String(trackId);
        const onResolved = (e) => {
            if (e?.detail?.trackId === tid && e.detail.youtubeId) {
                setResolvedYoutubeId(e.detail.youtubeId);
                setFellToSample(false); // a working stream landed — leave the sample
            }
        };
        window.addEventListener('djpal-track-link-resolved', onResolved);
        return () => window.removeEventListener('djpal-track-link-resolved', onResolved);
    }, [trackId]);

    // Detect the fall-to-sample. The player stashes globals + fires
    // player-track-change whenever the active source changes; if THIS row's
    // sample is what's playing and there's no full-stream originalUrl, playback
    // fell back and we should draw the sample.
    useEffect(() => {
        if (!src) { setFellToSample(false); return undefined; }
        const check = (musicSrc, origUrl) => {
            if (musicSrc && musicSrc === src && !origUrl) setFellToSample(true);
            else if (origUrl && musicSrc === src) setFellToSample(false);
        };
        setFellToSample(false);
        check(window.__currentPlayerMusicSrc || null, window.__currentPlayerOriginalUrl || null);
        const onChange = (e) => check(e?.detail?.musicSrc || null, e?.detail?.originalUrl || null);
        window.addEventListener('player-track-change', onChange);
        return () => window.removeEventListener('player-track-change', onChange);
    }, [src]);

    const effectiveYoutubeId = resolvedYoutubeId || youtubeId;
    // Manual takedown — never derive a YT/SC URL, so the peaks path falls
    // back to the sample. Mirrors DJPalAudioPreview's gate so a blocked
    // track never triggers a yt-dlp download even for waveform peaks.
    // SAMPLE mode applies the same null-URL gate to ALL tracks: the waveform
    // is built from the Apple preview only, never a yt-dlp / SC download.
    const originalUrl = (playbackBlocked || sampleOnly || fellToSample)
        ? null
        : adoptedUrl
            || (effectiveYoutubeId && effectiveYoutubeId !== '-1'
                ? `https://www.youtube.com/watch?v=${effectiveYoutubeId}`
                : soundcloudUrl || null);

    const cacheKey = originalUrl || src || null;
    const hasSrc = !!(src || originalUrl);

    // Reset readiness SYNCHRONOUSLY when the peaks source flips (e.g. a row
    // upgrades from the Apple sample to its resolved YouTube track). The old
    // reset lived in a post-render effect, which left exactly one render where
    // the row still rendered the PREVIOUS source's waveform (is-ready, fake
    // hidden) on the new track — the "I see another waveform for a sec" flash,
    // confirmed in the wf logs (key flips while ready stays true). React
    // supports setState-during-render guarded by a changed-key check.
    if (cacheKey !== loadedKey) {
        setLoadedKey(cacheKey);
        if (ready) setReady(false);
        if (failed) setFailed(false);
        if (sampleWaitElapsed) setSampleWaitElapsed(false);
        // Source upgrade (sample → resolved YT track): fade the new paint in
        // even if it's served from cache — the fade hides the swap flicker.
        instantRef.current = false;
    }

    const getColors = useCallback(() => {
        const canvas = document.createElement('canvas');
        canvas.height = 60;
        const ctx = canvas.getContext('2d');
        const wave = ctx.createLinearGradient(0, 0, 0, 60);
        // Shared palette for BOTH themes: a mid-grey gradient for the unplayed
        // bars — visible against both the dark and the light card — plus a clear
        // cyan for the played portion. Keeps the waveform identical in either
        // theme (the dark-theme played already read as ~#00bcd4).
        wave.addColorStop(0, '#a6abb0');
        wave.addColorStop(1, '#8d9297');
        // DESIGN V2: the played portion takes the accent token (amber), read at
        // paint time so the flag and the theme both apply.
        if (document.documentElement.dataset.design === 'v2') {
            const css = getComputedStyle(document.documentElement);
            const accent = css.getPropertyValue('--color-accent').trim();
            // Unplayed bars are ink at a third (the --v2-wave token), not the
            // classic mid-grey gradient, which read brown on the warm page.
            const unplayed = css.getPropertyValue('--v2-wave').trim();
            const top = css.getPropertyValue('--v2-wave-played-top').trim();
            const bottom = css.getPropertyValue('--v2-wave-played-bottom').trim();
            if (accent) {
                let played = accent;
                if (top && bottom) {
                    // The played bars are lit from the top, as classic's cyan was.
                    const h = document.documentElement.dataset.design === 'v2' ? 80 : 60;
                    played = ctx.createLinearGradient(0, 0, 0, h);
                    played.addColorStop(0, top); played.addColorStop(1, bottom);
                }
                return { waveColor: unplayed || wave, progressColor: played };
            }
        }
        return { waveColor: wave, progressColor: '#00bcd4' };
    }, []);

    // Cap how long a FAKE row holds on the shimmer waiting for a lazy YouTube
    // resolve before it falls back to drawing the sample, so a genuinely
    // sample-only track still gets a waveform. If the resolve lands first,
    // effectiveYoutubeId flips → this cleans up → the YouTube waveform draws
    // and the sample is never shown.
    useEffect(() => {
        if (!lazy || !shouldLoad || playbackBlocked || effectiveYoutubeId || sampleWaitElapsed) return undefined;
        const id = setTimeout(() => setSampleWaitElapsed(true), 5000);
        return () => clearTimeout(id);
    }, [lazy, shouldLoad, playbackBlocked, effectiveYoutubeId, sampleWaitElapsed]);

    useEffect(() => {
        if (!hasSrc || !shouldLoad || !containerRef.current) return;

        let cancelled = false;
        let rendered = false;

        const createWs = (peaks, duration, immediate = false) => {
            if (cancelled || rendered || !containerRef.current) return;
            // Reserve our slot synchronously so a second concurrent caller
            // (e.g. load() resolves AND a cache event fires for the same
            // row) bails on the `rendered` check instead of queueing a
            // second WaveSurfer instance.
            rendered = true;
            const create = () => {
                if (cancelled || !containerRef.current) return;
                const { waveColor, progressColor } = getColors();
                try {
                    const ws = WaveSurfer.create({
                        container: containerRef.current,
                        waveColor, progressColor,
                        cursorWidth: 0, barWidth: 2, barGap: 2, barRadius: 1,
                        // Design v2: 80px so the stack (title block + wave) fills the 132px art; classic 60.
                        height: document.documentElement.dataset.design === 'v2' ? 80 : 60, normalize: true, interact: false,
                        peaks: peaks ? [peaks] : undefined,
                        duration: duration || undefined,
                        hideScrollbar: true,
                    });
                    // If this row's track is ALREADY the active player track
                    // (remount after back-nav mid-playback), seed the progress
                    // before the first visible paint. The RAF tick only seeks
                    // after activation + ready, which used to leave the first
                    // painted frames all-grey and then pop the played portion
                    // in — the visible tail of the back-nav "flash".
                    const seekToLive = () => {
                        const audio = window.musicPlayerInstance;
                        const activeMusic = window.__currentPlayerMusicSrc || null;
                        const activeOrig = window.__currentPlayerOriginalUrl || null;
                        const activeTid = window.__currentPlayerTrackId || null;
                        const isActive = (src && activeMusic === src)
                            || (originalUrl && activeOrig === originalUrl)
                            || (trackId != null && activeTid === String(trackId));
                        if (!isActive || !audio || !(audio.duration > 0)) return;
                        try { ws.seekTo(Math.min(1, audio.currentTime / audio.duration)); } catch { /* not fatal */ }
                    };
                    seekToLive();
                    ws.on('ready', () => { if (!cancelled) { seekToLive(); setReady(true); } });
                    ws.on('error', () => { if (!cancelled) setFailed(true); });
                    wsRef.current = ws;
                } catch {
                    if (!cancelled) setFailed(true);
                }
            };
            // Cache-served paints (remount after back-nav) skip the one-per-
            // frame stagger: WaveSurfer.create with pre-decoded peaks is cheap,
            // and the stagger's per-row frame delay reads as a visible ripple.
            if (immediate) create();
            else staggerCreateWs(create);
        };

        const load = async () => {
            if (cancelled || rendered || !containerRef.current) return;

            // FAKE: while a lazy YouTube resolve may still land (no youtube URL
            // yet), hold the shimmer instead of drawing the sample — INCLUDING a
            // cached sample — so we don't flash the sample then swap to the real
            // track. originalUrl flips when the resolve lands → effect re-runs.
            // ...but once playback actually fell to the sample, draw it now
            // instead of holding — the resolve already failed.
            if (lazy && !originalUrl && !playbackBlocked && !sampleWaitElapsed && !fellToSample && src) {
                return;
            }

            // Cache hit — instant. Share with WaveformOverlay/MixFeedWaveform.
            if (cacheKey) {
                const cached = getCachedDecoded(cacheKey);
                if (cached) {
                    renderedRealRef.current = true;
                    instantRef.current = true;
                    createWs(cached.peaks, cached.duration, true);
                    return;
                }
            }

            // Both FULL and FAKE use the SAME fast yt-dlp peaks download here.
            // FAKE differs only in WHEN it runs (lazily, on play — see the
            // shouldLoad gate above), not in how it fetches. yt-dlp downloads
            // at full speed; a plain fetch of the googlevideo CDN URL would be
            // throttled to ~playback speed (30-50s), so we do NOT decode the
            // player's live stream here.
            if (originalUrl) {
                const entry = await fetchDecodedPeaks(originalUrl);
                if (cancelled || rendered || !containerRef.current) return;
                if (entry) {
                    renderedRealRef.current = true;
                    createWs(entry.peaks, entry.duration);
                    return;
                }
                // fall through to src
            }
            if (src) {
                try {
                    // no-store: the player streams this same URL with Range
                    // requests (sparse cache entry) — a cached full-body fetch of
                    // it fails with ERR_CACHE_OPERATION_NOT_SUPPORTED.
                    const response = await fetch(src, { cache: 'no-store' });
                    if (cancelled || rendered || !containerRef.current) return;
                    const buffer = await response.arrayBuffer();
                    if (cancelled || rendered || !containerRef.current) return;

                    // OfflineAudioContext: decode-only work must not consume
                    // one of Chromium's ~6 hardware-context slots (see
                    // peaksCache._decodeAndCache).
                    const audioCtx = new OfflineAudioContext(1, 8000, 8000);
                    const decoded = await audioCtx.decodeAudioData(buffer);
                    if (cancelled || rendered || !containerRef.current) return;

                    // Downsample BEFORE handing off, so the multi-MB AudioBuffer
                    // can be GC'd. A 30s Apple sample is small (~1MB) but the
                    // WaveSurfer instance holds the array for the row's lifetime,
                    // and many mounted rows × full-res buffers compounds quickly.
                    const peaks = downsamplePeaks(decoded.getChannelData(0));
                    if (cacheKey) setCachedDecoded(cacheKey, { peaks, duration: decoded.duration });
                    // Sample-derived paint only when there IS a real-peaks
                    // source we fell back from; sample-only rows (no
                    // originalUrl) ARE the real deal for their track.
                    renderedRealRef.current = !originalUrl;
                    createWs(peaks, decoded.duration);
                } catch {
                    if (!cancelled) setFailed(true);
                }
            } else {
                setFailed(true);
            }
        };

        // Listen for cache-populated events so we can short-circuit the
        // withLimit queue. The bottom player's WaveformOverlay bypasses
        // withLimit and can fill the cache mid-flight while we're still
        // waiting for a feed-load slot — without this listener, the For
        // You row for the clicked track stays blank until a slot opens.
        const onPeaksCached = (e) => {
            if (cancelled) return;
            if (!cacheKey || e?.detail?.url !== cacheKey) return;
            const entry = getCachedDecoded(cacheKey);
            if (!entry) return;
            if (!rendered) {
                renderedRealRef.current = true;
                createWs(entry.peaks, entry.duration);
                return;
            }
            // Real peaks arrived AFTER this row painted its sample/silhouette
            // fallback (slow queued download — the "waveform showed in the
            // player at 2:00 but never in For You" gap). Re-run the load
            // effect: cleanup tears down the fallback, the cache-hit branch
            // paints the real waveform.
            if (!renderedRealRef.current) setLoadNonce((n) => n + 1);
        };
        window.addEventListener('wf-peaks-cached', onPeaksCached);

        withLimit(load);

        // readyRef, not `ready`: the closure captures `ready` at effect
        // creation, so a re-run after a successful paint (loadNonce bump)
        // used to create a permanently no-op timer — and a failing reload
        // then left a blank is-ready strip with no timeout at all.
        const timeout = setTimeout(() => { if (!cancelled && !readyRef.current) setFailed(true); }, 30000);

        return () => {
            cancelled = true;
            window.removeEventListener('wf-peaks-cached', onPeaksCached);
            clearTimeout(timeout);
            if (wsRef.current) {
                try { wsRef.current.unAll(); wsRef.current.destroy(); } catch {}
                wsRef.current = null;
            }
        };
    // Re-run when originalUrl flips (e.g. lazy resolve landed and gave us a
    // youtubeId mid-mount) so the waveform repaints with the YT peaks
    // instead of staying on the sample silhouette. setReady(false) on the
    // transition so the opacity fade-in plays again, hiding the flicker.
    }, [hasSrc, originalUrl, shouldLoad, loadNonce, sampleWaitElapsed]); // eslint-disable-line react-hooks/exhaustive-deps

    // Playback progress tracking
    useEffect(() => {
        const stopRaf = () => {
            if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
        };

        const tick = () => {
            if (!activeRef.current) { stopRaf(); return; }
            const audio = window.musicPlayerInstance;
            // ws may not be ready yet on the first ticks after mount-time
            // activation (createWs is async) — keep polling and seek only
            // once it's available.
            if (audio && wsRef.current) {
                const dur = audio.duration;
                if (dur > 0) {
                    try { wsRef.current.seekTo(Math.min(1, audio.currentTime / dur)); } catch {}
                }
            }
            rafRef.current = requestAnimationFrame(tick);
        };

        const activate = () => {
            activeRef.current = true;
            // FAKE mode defers the real-waveform load until play. The play-click
            // (onRowPlay) already kicked the yt-dlp peaks download; this is the
            // safety net for the case where the row became active WITHOUT a
            // click on this instance (e.g. mounted while already playing, or
            // auto-advance) — just make sure the load is enabled.
            if (lazy) setShouldLoad(true);
            stopRaf();
            rafRef.current = requestAnimationFrame(tick);
        };

        const handler = (e) => {
            const { musicSrc, originalUrl: activeOriginalUrl, trackId: activeTrackId } = e.detail;
            const matchesById = trackId != null && activeTrackId != null && activeTrackId === String(trackId);
            const matches = (src && musicSrc === src)
                || (originalUrl && activeOriginalUrl === originalUrl)
                || matchesById;

            if (matches) {
                // Matched by id but not by URL → the player is streaming a
                // different link for this track (dead DB link replaced at
                // play time). Adopt it as the peaks source.
                if (matchesById && activeOriginalUrl && activeOriginalUrl !== originalUrl) {
                    setAdoptedUrl(activeOriginalUrl);
                }
                activate();
            } else if (activeRef.current) {
                // Different track became active — reset this waveform
                activeRef.current = false;
                stopRaf();
                try { if (wsRef.current) wsRef.current.seekTo(0); } catch {}
            }
        };

        // Sync up if a track is already playing when this row mounts (e.g.
        // user navigated away and came back). The MusicPlayer stashes the
        // active musicSrc/originalUrl globals on every track change for
        // exactly this case — no event arrives because the change happened
        // before this component mounted.
        const audio = window.musicPlayerInstance;
        const activeMusicSrc = window.__currentPlayerMusicSrc || null;
        const activeOriginalUrl = window.__currentPlayerOriginalUrl || null;
        const activeTrackId = window.__currentPlayerTrackId || null;
        const audioHasSrc = !!(audio && audio.src);
        const matchesActive = audioHasSrc && (
            (src && activeMusicSrc === src)
            || (originalUrl && activeOriginalUrl === originalUrl)
            || (trackId != null && activeTrackId === String(trackId))
        );
        if (matchesActive) activate();

        window.addEventListener('player-track-change', handler);
        return () => {
            window.removeEventListener('player-track-change', handler);
            stopRaf();
        };
    }, [src, originalUrl, trackId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Click: seek if this track is active, otherwise start playing it
    const handleClick = useCallback((e) => {
        if (!ready || !containerRef.current) return;
        e.stopPropagation();
        if (activeRef.current && window.musicPlayerInstance) {
            const rect = containerRef.current.getBoundingClientRect();
            const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            const dur = window.musicPlayerInstance.duration;
            if (dur > 0) {
                window.musicPlayerInstance.currentTime = ratio * dur;
                try { if (wsRef.current) wsRef.current.seekTo(ratio); } catch {}
            }
        } else {
            // Not active — trigger play
            window.dispatchEvent(new CustomEvent('djpal-row-play', { detail: previewId }));
        }
    }, [ready, previewId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Theme change
    useEffect(() => {
        const observer = new MutationObserver(() => {
            if (wsRef.current) {
                const { waveColor, progressColor } = getColors();
                wsRef.current.setOptions({ waveColor, progressColor });
            }
        });
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        return () => observer.disconnect();
    }, [getColors]);

    // On failure keep the (invisible) container MOUNTED instead of returning
    // null: nulling it while the effect stays alive made the late
    // wf-peaks-cached rescue bail silently in createWs (!containerRef.current)
    // — peaks arrived but the row stayed blank forever. An unready container
    // is opacity-0 and handleClick no-ops, so it renders/behaves identically
    // to the old null. failed only gates the loader now.
    if (!hasSrc) return null;

    // FAKE mode: once play kicks off the load (shouldLoad) and until the real
    // waveform paints, show the same shimmer loader the mixes use — over the
    // detailed fake (the CSS hides the fake while the loader is present). This
    // gives the fake → loader → real sequence instead of an abrupt swap. FULL/
    // SAMPLE eager-load with no loader (historical blank-then-real behavior).
    const showLoader = lazy && shouldLoad && !ready && !failed && !activeAtMount;

    return (
        <>
            <div
                className={`feed-waveform-real${ready ? ' is-ready' : ''}${activeAtMount && !failed ? ' is-active-start' : ''}`}
                ref={containerRef}
                onClick={handleClick}
                style={{ opacity: ready ? 1 : 0, transition: instantRef.current ? 'none' : 'opacity 0.3s', cursor: 'pointer' }}
            />
            {showLoader && (
                <div className="feed-waveform-loader" aria-label="Loading waveform">
                    <div className="feed-waveform-loader-bars" />
                </div>
            )}
        </>
    );
};

export default React.memo(FeedWaveform);
